import { useState } from "react";
import { MessageSquare, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useAnalytics } from "@/hooks/useAnalytics";
import { supabase } from "@/integrations/supabase/client";
import { useLocation } from "react-router-dom";

const MAX_CHARS = 2000;

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const { track } = useAnalytics();
  const location = useLocation();

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error("Please log in to submit feedback.");
        return;
      }

      const res = await fetch(
        "https://zbdgjhhhnlfgsxwxavcj.supabase.co/functions/v1/send-feedback",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpiZGdqaGhobmxmZ3N4d3hhdmNqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAxOTQ4NzcsImV4cCI6MjA2NTc3MDg3N30.yfQsbm8mLqYg4X2Ku_RI0vN_qt4oI44Jo8TDsgzw0kE",
          },
          body: JSON.stringify({
            feedback: trimmed,
            page: location.pathname,
            userAgent: navigator.userAgent,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to send feedback");
      }

      track("feedback_submitted", {
        page: location.pathname,
        feedback_length: trimmed.length,
      });

      toast.success("Thank you for your feedback!");
      setText("");
      setOpen(false);
    } catch (e: any) {
      console.error("Feedback submission error:", e);
      toast.error(e.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Only show for authenticated users
  if (!user) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            className="rounded-full shadow-lg px-4 py-2 gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            aria-label="Send feedback"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="text-sm font-medium">Feedback</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="top" align="end" className="w-80 p-4">
          <p className="font-heading font-semibold text-sm mb-1 text-foreground">
            Share your feedback
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            Bug reports, feature ideas, or anything on your mind.
          </p>
          <Textarea
            placeholder="What's on your mind?"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            rows={4}
            className="resize-none text-sm mb-2"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {text.length}/{MAX_CHARS}
            </span>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!text.trim() || submitting}
              className="gap-1.5"
            >
              <Send className="w-3.5 h-3.5" />
              {submitting ? "Sending…" : "Send"}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
